import { Text, Flex, VStack, Box, Button, Spinner } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { ModalUserWorksheetsVariantProps, SharedModalProps } from 'types/Modal';
import { CloseIcon, FileIcon } from 'assets/icons/v2';
import { ProtectedRoutes } from 'types/Routes';
import { useNavigate } from 'react-router';
import { useGetClassUserWorksheets } from 'hooks/api/backend/managament/getClasses';
import { useGetWorksheetsByStudent } from 'hooks/api/backend/worksheets/useWorksheets';
import { NovaIcon } from 'components/ui/novaIcon';
import { CustomModal } from '../../CustomModal';
import { CustomModalContent } from '../../CustomModalContent';

type Props = SharedModalProps & ModalUserWorksheetsVariantProps;

export const UserWorksheetsModal = ({
  handleClose,
  isShown,
  classId,
  studentId,
}: Props): ReactElement => {
  const navigate = useNavigate();

  const shouldUseClassApi = !!classId;

  const { data: classWorksheets, isLoading: isLoadingClass } = useGetClassUserWorksheets(
    classId,
    studentId
  );

  const { data: studentWorksheets, isLoading: isLoadingStudent } = useGetWorksheetsByStudent(
    classId ? {} : { studentId }
  );

  const worksheets = shouldUseClassApi
    ? (classWorksheets?.worksheets ?? [])
    : (studentWorksheets?.worksheets ?? []);

  const isLoading = shouldUseClassApi ? isLoadingClass : isLoadingStudent;

  const hasWorksheets = worksheets.length > 0;

  const handleWorksheetClick = (id: string): void => {
    const params = new URLSearchParams({ mode: 'review', t: '1' });
    if (classId) params.set('classId', classId);
    if (studentId) params.set('studentId', studentId);
    navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${id}?${params.toString()}`);
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" size="md" hideHeader>
      <CustomModalContent
        p="30px"
        display="flex"
        flexDirection="column"
        gap="30px"
        overflowY="auto"
      >
        <Flex justify="space-between" align="flex-start">
          <VStack align="flex-start" gap="15px">
            <Text textStyle="h3.medium" color="text.primary">
              Arbeitsblatt öffnen
            </Text>
            <Text textStyle="body.m.regular" color="text.tertiary">
              Wähle ein Arbeitsblatt aus der Liste aus und öffne es zur Kontrolle.
            </Text>
          </VStack>

          <NovaIcon
            icon={CloseIcon}
            size="l"
            color="icon.secondary"
            aria-hidden
            cursor="pointer"
            onClick={handleClose}
          />
        </Flex>

        {isLoading ? (
          <Flex align="center" justify="center" minH="180px">
            <Spinner />
          </Flex>
        ) : (
          <VStack align="stretch" gap="l">
            {!hasWorksheets && (
              <Text textStyle="body.m.regular" color="text.tertiary">
                Keine Arbeitsblätter gefunden
              </Text>
            )}

            {worksheets.map((worksheet) => (
              <Flex key={worksheet.id} align="center" gap="30px">
                <Flex flex="1" align="center" gap="10px" py="2xs">
                  <Box
                    borderRadius="s"
                    p="2xs"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minW="26px"
                    h="26px"
                    bg="#F0EDFE"
                  >
                    <FileIcon color="#4D3C9E" boxSize="18px" />
                  </Box>

                  <Box flex="1">
                    <Flex justify="space-between" align="center">
                      <Text textStyle="body.m.medium" color="text.primary">
                        {worksheet.title}
                      </Text>

                      <Text textStyle="body.xs.regular" color="text.secondary">
                        {worksheet.progressInPercent}%
                      </Text>
                    </Flex>

                    <Box mt="6px" bg="background.canvas" h="2px" borderRadius="2px">
                      <Box
                        h="2px"
                        bg="background.accent"
                        borderRadius="2px"
                        width="[{worksheet.progressInPercent}%"
                      />
                    </Box>
                  </Box>
                </Flex>

                {worksheet.progressInPercent !== 0 && (
                  <Button
                    variant="tertiary"
                    size="m"
                    textStyle="body.s.medium"
                    color="text.accent"
                    p="15px 8px"
                    onClick={() => handleWorksheetClick(worksheet.id)}
                  >
                    Öffnen
                  </Button>
                )}
              </Flex>
            ))}
          </VStack>
        )}
      </CustomModalContent>
    </CustomModal>
  );
};
